import React, { memo } from 'react';
import { TextField, TextArea, View } from '@adobe/react-spectrum';
import { FormField } from '../../store/slice';
import styles from '../FormField.module.scss';

interface TextFieldComponentProps {
  field: FormField;
  value: any;
  onChange: (name: string, value: any) => void;
  onBlur?: (name: string) => void;
  validationState?: 'valid' | 'invalid';
  errorMessage?: string;
}

function getInputPrependLabel(field: FormField): string | null {
  if (typeof field.inputPrepend === 'string' && field.inputPrepend.trim()) {
    return field.inputPrepend.trim();
  }
  if (field.inputPrepend === true && field.inputPrependLabel?.trim()) {
    return field.inputPrependLabel.trim();
  }
  return null;
}

/**
 * Component for rendering text input fields (text and textarea)
 */
export const TextFieldComponent: React.FC<TextFieldComponentProps> = memo(
  ({ field, value, onChange, onBlur, validationState }) => {
    const effectiveValidationState =
      validationState === 'invalid' ? 'invalid' : undefined;
    const prependLabel = getInputPrependLabel(field);
    const showInputPrepend = Boolean(prependLabel);

    if (field.type === 'textarea') {
      return (
        <View
          UNSAFE_className={`${styles.formField} ${
            validationState === 'invalid' ? styles.error : ''
          }`}
        >
          <div className={styles.fieldLabel}>
            {field.label}
            {field.required && <span className={styles.required}> *</span>}
          </div>
          <TextArea
            UNSAFE_className={styles.textArea}
            key={field.id}
            name={field.name}
            value={value || ''}
            onChange={val => onChange(field.name, val)}
            onBlur={onBlur ? () => onBlur(field.name) : undefined}
            validationState={effectiveValidationState}
            data-testid={`text-area-${field.name
              .replace(/[^a-zA-Z0-9]/g, '-')
              .toLowerCase()}`}
          />
        </View>
      );
    }

    const isNameField = field.name === 'name';
    const wrapperClass = [
      styles.formField,
      validationState === 'invalid' ? styles.error : '',
      showInputPrepend
        ? styles.eolNameWithPrepend
        : isNameField
        ? styles.nameTextFieldWrapper
        : ''
    ]
      .filter(Boolean)
      .join(' ');

    const textFieldKey = `${field.id}-${
      showInputPrepend ? prependLabel : 'plain'
    }`;

    const textField = (
      <TextField
        UNSAFE_className={`${styles.textField} ${
          showInputPrepend ? styles.eolNameInput : ''
        } ${isNameField && !showInputPrepend ? styles.nameTextField : ''}`}
        key={textFieldKey}
        name={field.name}
        value={value || ''}
        onChange={val =>
          onChange(
            field.name,
            field.maxLength != null ? val.slice(0, field.maxLength) : val
          )
        }
        onBlur={onBlur ? () => onBlur(field.name) : undefined}
        type={field.type === 'number' ? 'number' : 'text'}
        validationState={
          showInputPrepend ? undefined : effectiveValidationState
        }
        autoFocus={isNameField}
        aria-label={field.label}
        maxLength={field.maxLength}
        data-testid={`text-field-${field.name
          .replace(/[^a-zA-Z0-9]/g, '-')
          .toLowerCase()}`}
      />
    );

    return (
      <View UNSAFE_className={wrapperClass}>
        <div className={styles.fieldLabel}>
          {field.label}
          {field.required && <span className={styles.required}> *</span>}
        </div>

        {showInputPrepend ? (
          <div className={styles.eolNameFieldGroup}>
            <span className={styles.eolNamePrependText} aria-hidden="true">
              {prependLabel}
            </span>
            {textField}
          </div>
        ) : (
          textField
        )}
      </View>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.value !== nextProps.value) return false;
    if (prevProps.validationState !== nextProps.validationState) {
      return false;
    }
    if (
      getInputPrependLabel(prevProps.field) !==
      getInputPrependLabel(nextProps.field)
    ) {
      return false;
    }
    if (prevProps.field.id !== nextProps.field.id) return false;
    return true;
  }
);
